/**
 * DisputeAgentPanel — Wired to real BE API (5-state model).
 *
 * Receives state from the useDisputeAgent hook.
 * States: none → active ↔ awaiting-vendor → resolved / withdrawn
 *
 * PRD: https://linear.app/liftai/document/prd-dispute-management-agent-dd09a381716d
 * Linear: LAI-1508
 */

import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import GavelIcon from '@mui/icons-material/Gavel';
import InboxIcon from '@mui/icons-material/Inbox';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RadarIcon from '@mui/icons-material/Radar';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  keyframes,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { type FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { useNavigate } from 'react-router';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import CurrencySymbol from '~/components/common/CurrencySymbol';
import { UploadDocumentType } from '~/components/uploadDocument/AddManuallyDropdown';
import type { LiftAIDocumentTagged } from '~/components/uploadDocument/SelectDocumentTypeDialog';
import SelectUploadDocumentTypeDialog from '~/components/uploadDocument/SelectDocumentTypeDialog';
import type { LiftAIDocumentUntagged } from '~/components/uploadDocument/UploadDocumentDialogFlow';
import type { Attachment as AppAttachment } from '~/data/schemas/attachment';
import type { Invoice, InvoiceInsight } from '~/data/schemas/invoice';
import { InvoiceKind } from '~/data/schemas/invoice';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';
import { formatCurrency, toDateFormatMMddyyyy } from '~/utils/format';
import { buildUrlForInvoices } from '~/utils/urlBuilder';

import type {
  AgentSuggestion,
  Attachment,
  DisputeAgentState,
  EmailDraft,
  ResolutionType,
  TimelineEntry,
  TimelineEntryType,
  VendorPosition,
} from './types';
import {
  DELIVERY_STATUS_CONFIG,
  DISPUTE_STATUS_COLOR,
  getDisputeStatusLabel,
  RESOLUTION_TYPE_LABEL,
  VENDOR_POSITION_CONFIG,
} from './types';

// ── Animations ──────────────────────────────────────────────────────────

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

// ── Timeline Helpers ────────────────────────────────────────────────────

const TIMELINE_CONFIG: Record<
  TimelineEntryType,
  {
    label: string;
    icon: typeof EmailIcon;
    color: 'primary' | 'info' | 'warning' | 'success' | 'error';
  }
> = {
  'dispute-email-sent': { label: 'Dispute Sent', icon: EmailIcon, color: 'primary' },
  'data-request-sent': { label: 'Data Requested', icon: SendIcon, color: 'info' },
  'follow-up-sent': { label: 'Follow-up Sent', icon: ForwardToInboxIcon, color: 'info' },
  'vendor-response': { label: 'Vendor Response', icon: InboxIcon, color: 'warning' },
  // A non-vendor reply (auto-reply/out-of-office or a consultant's own reply).
  // Neutral styling so it reads clearly as "not a real vendor response" rather
  // than borrowing the warning-colored 'Vendor Response' treatment.
  'non-vendor-reply': { label: 'Other Reply', icon: MoreHorizIcon, color: 'info' },
  resolution: { label: 'Resolution', icon: CheckCircleIcon, color: 'success' },
  'adjusted-invoice-linked': {
    label: 'Adjusted Invoice Linked',
    icon: AttachFileIcon,
    color: 'info',
  },
  'adjusted-invoice-approved': {
    label: 'Adjusted Invoice Approved',
    icon: CheckCircleIcon,
    color: 'success',
  },
  'adjusted-invoice-rejected': {
    label: 'Adjusted Invoice Rejected',
    icon: CloseIcon,
    color: 'error',
  },
};

// ── Inline form types ───────────────────────────────────────────────────

type ActiveForm = 'none' | 'resolve';

// =====================================================================
// SUB-VIEWS
// =====================================================================

// ── Suggestion Banner ───────────────────────────────────────────────────

const SuggestionBanner: FC<{
  suggestions: AgentSuggestion[];
  onAction: (action: string, payload?: Record<string, unknown> | null) => void;
  onWithdraw?: () => void;
  loading?: boolean;
  vendorResponseSummary?: string | null;
}> = ({ suggestions, onAction, onWithdraw, loading = false, vendorResponseSummary }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null);

  const primary = suggestions.find((s) => s.priority === 'primary');
  const secondary = suggestions.find((s) => s.priority === 'secondary');
  const overflow = suggestions.filter((s) => s.priority === 'overflow');

  // Check if this is a vendor response suggestion (has vendorPosition in payload)
  const vendorPosition = primary?.payload?.vendorPosition as VendorPosition | undefined;
  const confidence = primary?.payload?.confidence as number | undefined;
  const positionConfig = vendorPosition ? VENDOR_POSITION_CONFIG[vendorPosition] : null;

  if (!primary) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        bgcolor: 'primary.50',
        borderLeft: '3px solid',
        borderColor: 'primary.main',
        borderRadius: 2,
      }}
    >
      {/* Vendor Response Header (when vendorPosition is present) */}
      {positionConfig ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <InboxIcon sx={{ color: 'warning.main', fontSize: 18 }} />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Vendor Response Received
          </Typography>
          <Chip
            label={positionConfig.label}
            size="small"
            sx={{
              bgcolor: positionConfig.color,
              color: 'white',
              fontWeight: 500,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
          {confidence != null && confidence > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {Math.round(confidence * 100)}% confidence
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {/* Summary / Reason row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {loading ? (
          <CircularProgress size={20} sx={{ flexShrink: 0, mt: 0.25 }} />
        ) : (
          <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0, mt: 0.25 }} />
        )}
        <Box sx={{ flex: 1 }}>
          {loading ? (
            <Typography variant="body2">Generating draft...</Typography>
          ) : (
            <>
              <Typography variant="body2" fontWeight={500}>
                {primary.reason}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Action buttons row */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={loading}
          onClick={() => onAction(primary.action, primary.payload)}
          sx={{ ...BUTTON_SX, textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          {primary.label}
        </Button>
        {secondary ? (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            disabled={loading}
            onClick={() => onAction(secondary.action, secondary.payload)}
            sx={{ ...BUTTON_SX, textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {secondary.label}
          </Button>
        ) : null}
        {overflow.length > 0 && (
          <>
            <IconButton
              size="small"
              disabled={loading}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ ml: 0.5 }}
            >
              <MoreHorizIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              {overflow.map((a) => (
                <MenuItem
                  key={a.action}
                  onClick={() => {
                    onAction(a.action, a.payload);
                    setMenuAnchor(null);
                  }}
                  sx={{ fontSize: '0.85rem' }}
                >
                  {a.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {/* Vertical 3-dots menu with Withdraw option */}
        {onWithdraw ? (
          <>
            <IconButton
              size="small"
              disabled={loading}
              onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
              sx={{ ml: 0.5 }}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Menu
              anchorEl={moreMenuAnchor}
              open={Boolean(moreMenuAnchor)}
              onClose={() => setMoreMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  onWithdraw();
                  setMoreMenuAnchor(null);
                }}
                sx={{ fontSize: '0.85rem', color: 'error.main' }}
              >
                <CloseIcon sx={{ fontSize: 16, mr: 1 }} />
                Withdraw Dispute
              </MenuItem>
            </Menu>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

// ── Contract Citations ───────────────────────────────────────────────────

const ContractCitations: FC<{
  insights: InvoiceInsight[];
}> = ({ insights }) => {
  // Filter insights that have contract citations
  const citedInsights = useMemo(
    () => insights.filter((i) => i.contractSection || i.contractQuote),
    [insights],
  );

  if (citedInsights.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Contract Citations
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Referenced in this email:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {citedInsights.map((insight, index) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {insight.contractSection ? (
              <Chip
                label={insight.contractSection}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ alignSelf: 'flex-start' }}
              />
            ) : null}
            {insight.contractQuote ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic', pl: 0.5 }}
              >
                &ldquo;{insight.contractQuote}&rdquo;
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ── Email Draft Editor ──────────────────────────────────────────────────

const DraftEditor: FC<{
  draft: EmailDraft;
  invoiceAttachments?: { id: string; filename: string; url?: string }[];
  contractInsights?: InvoiceInsight[];
  sending?: boolean;
  onSend: (attachmentIds?: string[]) => void;
  onDiscard: () => void;
  onUpdate: (updates: Partial<Pick<EmailDraft, 'subject' | 'body' | 'to' | 'cc'>>) => Promise<void>;
}> = ({
  draft,
  invoiceAttachments = [],
  contractInsights = [],
  sending = false,
  onSend,
  onDiscard,
  onUpdate,
}) => {
  const [to, setTo] = useState(draft.to?.join(', ') ?? '');
  const [cc, setCc] = useState(draft.cc?.join(', ') ?? '');
  // Defaulted like `to`/`cc` above: a draft seeded by `initiate_dispute` but
  // never filled in by `generate_draft` carries only `fromEmail`, so these are
  // absent on the wire and would otherwise make the TextFields uncontrolled.
  const [subject, setSubject] = useState(draft.subject ?? '');
  const [body, setBody] = useState(draft.body ?? '');

  // No prop→state sync effect needed: the parent passes a fresh React
  // `key` whenever a new draft is generated (see draftGenerationKey in
  // DisputeAgentPanel), so this component unmounts and remounts with
  // the new draft as the useState initial values. updateDraft-driven
  // re-renders (which fire on every keystroke) reuse this mount, so
  // the user's in-progress edits aren't clobbered.

  // Attachments from BE draft
  const draftAttachments: Attachment[] = draft.attachments ?? [];

  // All invoice attachments are included by default
  const invoiceAttachmentIds = useMemo(
    () => invoiceAttachments.map((a) => a.id),
    [invoiceAttachments],
  );

  const draftLabel =
    draft.draftType === 'dispute'
      ? 'Dispute Email'
      : draft.draftType === 'data_request'
        ? 'Data Request'
        : 'Follow-up Email';

  // Parse and validate email addresses
  const parsedToEmails = useMemo(
    () =>
      to
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [to],
  );
  const hasValidTo = parsedToEmails.length > 0;

  // Parse CC emails
  const parsedCcEmails = useMemo(
    () =>
      cc
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [cc],
  );

  const handleSend = useCallback(async () => {
    if (!hasValidTo) return;
    // Save any edits before sending
    await onUpdate({
      subject,
      body,
      to: parsedToEmails,
      cc: parsedCcEmails,
    });
    // Pass invoice attachment IDs to be included in the email
    onSend(invoiceAttachmentIds.length > 0 ? invoiceAttachmentIds : undefined);
  }, [
    subject,
    body,
    parsedToEmails,
    parsedCcEmails,
    hasValidTo,
    onUpdate,
    onSend,
    invoiceAttachmentIds,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={600}>
          {draftLabel}
        </Typography>
        {draft.isAgentGenerated ? (
          <Chip label="AI Draft" size="small" color="primary" variant="outlined" />
        ) : null}
      </Box>

      {/* Email fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {draft.fromEmail ? (
          <TextField
            label="From"
            value={draft.fromEmail}
            size="small"
            fullWidth
            disabled
            InputProps={{
              readOnly: true,
              sx: { bgcolor: 'grey.50' },
            }}
          />
        ) : null}
        <TextField
          label="To"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          size="small"
          fullWidth
          required
          error={!hasValidTo}
          helperText={!hasValidTo ? 'At least one recipient email is required' : undefined}
        />
        <TextField
          label="CC"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          value={body}
          onChange={(e) => setBody(e.target.value)}
          multiline
          rows={10}
          fullWidth
          sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        />
      </Box>

      {/* Attachments section */}
      {draftAttachments.length > 0 || invoiceAttachments.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Attachments
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Draft attachments (read-only, managed by BE) */}
            {draftAttachments.map((attachment) => (
              <Box
                key={attachment.id}
                component="a"
                href={attachment.url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 20 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {attachment.filename}
                </Typography>
                <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Box>
            ))}
            {/* Invoice attachments */}
            {invoiceAttachments.map((attachment) => (
              <Box
                key={attachment.id}
                component="a"
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.5,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2">{attachment.filename}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {/* Contract Citations */}
      {draft.draftType === 'dispute' && contractInsights.length > 0 ? (
        <ContractCitations insights={contractInsights} />
      ) : null}

      {/* Send / Discard */}
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onDiscard}
          disabled={sending}
          sx={{ ...BUTTON_SX, textTransform: 'none' }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          size="small"
          disabled={!hasValidTo || sending}
          onClick={handleSend}
          sx={{ ...BUTTON_SX, textTransform: 'none' }}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </Button>
      </Box>
    </Box>
  );
};

// ── Timeline ────────────────────────────────────────────────────────────

export const TimelineEntryView: FC<{
  entry: TimelineEntry;
  isLast: boolean;
  showConnector: boolean;
}> = ({ entry, isLast, showConnector }) => {
  const config = TIMELINE_CONFIG[entry.type] ?? TIMELINE_CONFIG['vendor-response'];
  const isInbound = useMemo(() => entry.direction === 'inbound', [entry.direction]);

  // An outbound email that failed or is still in flight must not render with
  // the neutral "sent" label/icon, or the timeline would claim the vendor was
  // contacted when they weren't (AMP-541). Override the chip and icon for those
  // states; a delivered ('sent') entry keeps its per-type styling.
  const isFailed = entry.status === 'failed';
  const isPending = entry.status === 'pending';

  // Delivery outcome (SendGrid Event Webhook) is orthogonal to send status:
  // status = did the send leave our system; deliveryStatus = what happened after
  // SendGrid accepted it. Only decorate a genuinely-sent outbound entry whose
  // delivery later failed (bounce/drop/spam) — a 'failed'/'pending' entry already
  // shows its own state, and delivered/deferred/accepted need no chip noise. This
  // gives the "Dispute Sent" + "Bounced" reading (reached SendGrid, then failed)
  // instead of a single misleading chip (AMP-542).
  const deliveryConfig =
    entry.status === 'sent' && !isInbound && entry.deliveryStatus
      ? DELIVERY_STATUS_CONFIG[entry.deliveryStatus]
      : null;
  const deliveryFailed = deliveryConfig?.isFailure ?? false;
  const Icon = isFailed ? CloseIcon : config.icon;
  const chipColor = isFailed ? 'error' : isPending ? 'warning' : config.color;
  const chipLabel = isFailed
    ? `${config.label} — Failed`
    : isPending
      ? `${config.label} — Sending…`
      : config.label;

  // Get vendor position config if available (agent auto-categorizes responses)
  const positionConfig = useMemo(() => {
    if (entry.type === 'vendor-response' && entry.vendorPosition) {
      return VENDOR_POSITION_CONFIG[entry.vendorPosition as VendorPosition] ?? null;
    }
    return null;
  }, [entry.type, entry.vendorPosition]);

  return (
    <Box sx={{ position: 'relative', pb: isLast ? 0 : 3 }}>
      {showConnector ? (
        <Box
          sx={{ position: 'absolute', left: 15, top: 32, bottom: 0, width: 2, bgcolor: 'divider' }}
        />
      ) : null}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: `${chipColor}.main`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            zIndex: 1,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Chip label={chipLabel} size="small" color={chipColor} variant="outlined" />
            {/* Delivery failure: the send reached SendGrid but the vendor never
                received it (bounce/drop/spam). A second chip alongside the "Sent"
                chip reads as "sent, then bounced" (AMP-542). */}
            {deliveryFailed && deliveryConfig ? (
              <Chip label={deliveryConfig.label} size="small" color="error" />
            ) : null}
            {/* Show agent-categorized vendor position */}
            {positionConfig ? (
              <Chip
                label={positionConfig.label}
                size="small"
                sx={{
                  bgcolor: positionConfig.color,
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {toDateFormatMMddyyyy(entry.timestamp)}
            </Typography>
            {entry.sender ? (
              <Typography variant="caption" color="text.secondary">
                from {entry.sender}
              </Typography>
            ) : null}
          </Box>
          {entry.subject ? (
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {entry.subject}
            </Typography>
          ) : null}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: isInbound ? 'warning.50' : 'background.paper',
              maxHeight: 160,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}
            >
              {entry.body}
            </Typography>
          </Paper>
          {/* Send failure reason — why the vendor was not contacted */}
          {isFailed && (entry.failureReason || entry.failedAttachments?.length) ? (
            <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '0.8rem' }}>
              {entry.failureReason || 'The email could not be sent.'}
              {entry.failedAttachments?.length
                ? ` Missing attachments: ${entry.failedAttachments.join(', ')}.`
                : ''}
            </Typography>
          ) : null}
          {/* Partial attachment failure on a *sent* email: the vendor was
              contacted, but some evidence files could not be loaded and did not
              go out — surface exactly which ones (AMP-541). */}
          {!isFailed && entry.failedAttachments?.length ? (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontSize: '0.8rem' }}>
              Sent without {entry.failedAttachments.length} evidence file
              {entry.failedAttachments.length === 1 ? '' : 's'} that could not be loaded:{' '}
              {entry.failedAttachments.join(', ')}.
            </Typography>
          ) : null}
          {/* Delivery failure reason — why the vendor never received a sent email. */}
          {deliveryFailed && deliveryConfig ? (
            <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '0.8rem' }}>
              The vendor did not receive this email ({deliveryConfig.label.toLowerCase()}).
              {entry.deliveryStatusDetail ? ` ${entry.deliveryStatusDetail}` : ''}
            </Typography>
          ) : null}
          {/* Vendor Response Summary */}
          {entry.vendorResponseSummary ? (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontFamily: 'Roboto',
                fontStyle: 'italic',
                fontWeight: 200,
                color: 'text.secondary',
                fontSize: '0.8rem',
              }}
            >
              &ldquo;{entry.vendorResponseSummary}&rdquo;
            </Typography>
          ) : null}
          {/* Attachments */}
          {entry.attachments?.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {entry.attachments.map((attachment) => (
                <Chip
                  key={attachment.id}
                  component="a"
                  href={attachment.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                  label={attachment.filename}
                  size="small"
                  variant="outlined"
                  clickable
                  sx={{ textDecoration: 'none' }}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const Timeline: FC<{
  entries: TimelineEntry[];
  isMonitoring: boolean;
}> = ({ entries, isMonitoring }) => {
  if (entries.length === 0 && !isMonitoring) return null;

  const sorted = [...entries].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Communication History
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {sorted.map((entry, i) => (
        <TimelineEntryView
          key={entry.id}
          entry={entry}
          isLast={!isMonitoring && i === sorted.length - 1}
          showConnector={isMonitoring || i < sorted.length - 1}
        />
      ))}
      {isMonitoring ? (
        <Box sx={{ position: 'relative', pt: 3 }}>
          <Box
            sx={{
              position: 'absolute',
              left: 15,
              top: 0,
              height: 24,
              width: 2,
              bgcolor: 'divider',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                zIndex: 1,
                animation: `${pulse} 2s infinite`,
              }}
            >
              <RadarIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Chip label="Monitoring" size="small" color="primary" sx={{ fontWeight: 500 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Waiting for vendor response
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

// ── Resolution Form ─────────────────────────────────────────────────────

const ResolveForm: FC<{
  onSubmit: (type: ResolutionType, settledAmount: number, notes: string) => void;
  onCancel: () => void;
  initialType?: ResolutionType;
  initialSettledAmount?: number;
}> = ({ onSubmit, onCancel, initialType, initialSettledAmount }) => {
  const [type, setType] = useState<ResolutionType | ''>(initialType ?? '');
  const [settledAmount, setSettledAmount] = useState(initialSettledAmount ?? 0);
  const [notes, setNotes] = useState('');

  const showAmountField = type === 'credit-issued' || type === 'adjusted-invoice-received';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Resolve Dispute
        </Typography>
      </Box>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Resolution Type</InputLabel>
        <Select
          value={type}
          label="Resolution Type"
          onChange={(e) => {
            const v = e.target.value as ResolutionType;
            setType(v);
            if (v === 'vendor-rejected' || v === 'withdrawn') setSettledAmount(0);
          }}
        >
          {(Object.entries(RESOLUTION_TYPE_LABEL) as [ResolutionType, string][]).map(
            ([val, lbl]) => (
              <MenuItem key={val} value={val}>
                {lbl}
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>

      {type === 'vendor-rejected' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          &ldquo;Vendor Rejected&rdquo; means the dispute was unsuccessful and the original invoice
          amount stands. No savings will be recorded.
        </Alert>
      )}

      {showAmountField ? (
        <TextField
          label="Settled Amount"
          type="number"
          value={settledAmount}
          onChange={(e) => setSettledAmount(parseFloat(e.target.value) || 0)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CurrencySymbol />
              </InputAdornment>
            ),
          }}
          helperText="Final settled amount (0 if fully credited)"
        />
      ) : null}

      <TextField
        label="Notes"
        placeholder="Resolution notes..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        multiline
        rows={3}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onCancel}
          sx={{ ...BUTTON_SX, textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          size="small"
          disabled={!type}
          onClick={() => onSubmit(type as ResolutionType, settledAmount, notes)}
          sx={{ ...BUTTON_SX, textTransform: 'none' }}
        >
          Mark Resolved
        </Button>
      </Box>
    </Box>
  );
};

// ── Resolution Details (shown in terminal states) ───────────────────────

const ResolutionDetails: FC<{
  resolution: DisputeAgentState['resolution'];
  status: DisputeAgentState['status'];
  onReopen?: () => void;
  reopenLoading?: boolean;
}> = ({ resolution, status, onReopen, reopenLoading }) => {
  const { currencyCode } = useContext(UserContext);
  if (!resolution) return null;
  const isSuccess = status === 'resolved';
  const savings = parseFloat(resolution.savings) || 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: isSuccess ? 'success.50' : 'grey.50',
        borderColor: isSuccess ? 'success.main' : 'grey.300',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isSuccess ? (
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
          ) : (
            <CloseIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          )}
          <Typography variant="subtitle2" fontWeight={600}>
            {isSuccess ? 'Dispute Resolved' : 'Dispute Withdrawn'}
          </Typography>
        </Box>
        {onReopen ? (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={onReopen}
            disabled={reopenLoading}
            sx={{ ...BUTTON_SX, textTransform: 'none', fontSize: '0.75rem' }}
          >
            {reopenLoading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
            Reopen Dispute
          </Button>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Resolution
          </Typography>
          <Typography variant="body2">
            {RESOLUTION_TYPE_LABEL[resolution.resolutionType as ResolutionType] ??
              resolution.resolutionType}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Settled Amount
          </Typography>
          <Typography variant="body2">
            {formatCurrency({ value: parseFloat(resolution.settledAmount), currencyCode })}
          </Typography>
        </Box>
        {savings > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Savings
            </Typography>
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {formatCurrency({ value: savings, currencyCode })}
            </Typography>
          </Box>
        )}
      </Box>
      {resolution.notes ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {resolution.notes}
        </Typography>
      ) : null}
    </Paper>
  );
};

// =====================================================================
// MAIN PANEL
// =====================================================================

interface DisputeAgentPanelProps {
  invoice?: Invoice;
  canInitiateDispute: boolean;
  readOnly?: boolean;
  agentState: DisputeAgentState | null;
  timeline: TimelineEntry[];
  loading: boolean;
  error: string | null;
  onInitiateDispute: () => Promise<void>;
  onGenerateDraft: (draftType: 'dispute' | 'data_request' | 'follow_up') => Promise<void>;
  onUpdateDraft: (
    updates: Partial<Pick<EmailDraft, 'subject' | 'body' | 'to' | 'cc'>>,
  ) => Promise<void>;
  onDiscardDraft: () => void;
  onSendEmail: (attachmentIds?: string[]) => Promise<void>;
  onResolve: (resolutionType: string, settledAmount: number, notes?: string) => Promise<void>;
  onWithdraw: (reason?: string) => Promise<void>;
  onReopen: (reason?: string) => Promise<void>;
  onRefreshState: () => void;
  onRefreshTimeline: () => void;
}

const DisputeAgentPanel: FC<DisputeAgentPanelProps> = ({
  invoice,
  canInitiateDispute,
  readOnly = false,
  agentState,
  timeline,
  loading,
  error,
  onInitiateDispute,
  onGenerateDraft,
  onUpdateDraft,
  onDiscardDraft,
  onSendEmail,
  onResolve,
  onWithdraw,
  onReopen,
  onRefreshState,
  onRefreshTimeline,
}) => {
  const { currencyCode } = useContext(UserContext);

  // The shell every state of the panel renders into: the grey ground and padding of the
  // other tabs, with the whole column scrolling as one.
  const shellSx = {
    p: 3,
    flexGrow: 1,
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    bgcolor: 'grey.50',
  } as const;

  // On the grey ground each section is a white card. Only the sections that render bare
  // need it — the summary and resolution blocks are already `Paper` surfaces.
  const sectionCardSx = {
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
  } as const;

  const [activeForm, setActiveForm] = useState<ActiveForm>('none');
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);
  const { showSnackbar } = useLAISnackbar();
  const navigate = useNavigate();
  const { track } = useAnalytics();

  // Fire once per invoice-id whenever the panel becomes visible with state
  // loaded. Captures status at view time so funnel cohorts (e.g. "users
  // who landed on a panel in awaiting-vendor state") are queryable.
  const trackedInvoiceIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = invoice?.id;
    if (!id || !agentState) return;
    if (trackedInvoiceIdRef.current === id) return;
    trackedInvoiceIdRef.current = id;
    track('dispute_panel_viewed', { invoice_id: id, status: agentState.status });
  }, [invoice?.id, agentState, track]);

  // EmailDraft has no stable id, and `updateDraft` revalidates the SWR
  // cache on every keystroke → the prop reference changes constantly.
  // We bump this counter only when a *new* draft is generated, and use
  // it as the DraftEditor's React key so the form remounts (resetting
  // useState defaults from the new draft) only on regeneration — not
  // on every character the user types.
  const draftGenerationKey = useRef(0);
  const [resolvePreFill, setResolvePreFill] = useState<{
    type?: ResolutionType;
    settledAmount?: number;
  }>({});
  const [adjustedUploadDocs, setAdjustedUploadDocs] = useState<LiftAIDocumentUntagged[] | null>(
    null,
  );

  const status = useMemo(() => agentState?.status ?? 'none', [agentState]);
  const isActive = useMemo(() => !['none', 'resolved', 'withdrawn'].includes(status), [status]);
  const hasDraft = useMemo(
    () => agentState?.currentDraft !== null && agentState?.currentDraft !== undefined,
    [agentState],
  );

  const suggestions = useMemo(() => agentState?.suggestedActions ?? [], [agentState]);
  const hasPrimarySuggestion = useMemo(
    () => suggestions.some((s) => s.priority === 'primary'),
    [suggestions],
  );
  const isMonitoring = useMemo(
    () => status === 'awaiting-vendor' && !hasPrimarySuggestion,
    [status, hasPrimarySuggestion],
  );

  // Get the latest vendor response summary from timeline (for suggestion banner)
  const latestVendorResponseSummary = useMemo(() => {
    const vendorResponses = timeline
      .filter((e) => e.type === 'vendor-response')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return vendorResponses[0]?.vendorResponseSummary ?? null;
  }, [timeline]);

  // Clear action loading when draft becomes available or on error
  useEffect(() => {
    if (hasDraft || error) {
      setActionLoading(false);
    }
  }, [hasDraft, error]);

  // Clear sending state when draft is cleared (email sent successfully) or on error
  useEffect(() => {
    if (!hasDraft || error) {
      setSendingEmail(false);
    }
  }, [hasDraft, error]);

  const handleSendEmail = useCallback(
    async (attachmentIds?: string[]) => {
      setSendingEmail(true);
      try {
        await onSendEmail(attachmentIds);
        showSnackbar('Dispute email sent successfully', { variant: 'success' });
      } catch (e) {
        // Extract and display error message
        const errorMessage =
          e instanceof Error ? e.message : 'Failed to send dispute email. Please try again.';
        showSnackbar(errorMessage, { variant: 'error' });
        setSendingEmail(false);
      }
    },
    [onSendEmail, showSnackbar],
  );

  const handleAction = useCallback(
    async (action: string, payload?: Record<string, unknown> | null) => {
      if (invoice?.id) {
        const matched = suggestions.find((s) => s.action === action);
        track('dispute_suggestion_clicked', {
          invoice_id: invoice.id,
          action,
          priority: matched?.priority ?? 'primary',
        });
      }
      switch (action) {
        case 'initiate_dispute': {
          setActionLoading(true);
          await onInitiateDispute();
          await onGenerateDraft('dispute');
          draftGenerationKey.current += 1;
          break;
        }
        case 'request_data':
          setActionLoading(true);
          await onInitiateDispute();
          await onGenerateDraft('data_request');
          draftGenerationKey.current += 1;
          break;
        case 'generate_draft':
          setActionLoading(true);
          await onGenerateDraft(
            (payload?.draftType as 'dispute' | 'data_request' | 'follow_up') ?? 'dispute',
          );
          draftGenerationKey.current += 1;
          break;
        case 'send_email':
          void handleSendEmail();
          break;
        case 'upload_adjusted_invoice': {
          const detectedAttachments = payload?.detectedAttachments as
            | Array<{ attachmentId: string; filename: string }>
            | undefined;
          const timelineEntryId = payload?.timelineEntryId as string | undefined;
          if (!detectedAttachments?.length || !timelineEntryId) break;

          const targetAttachmentId = detectedAttachments[0].attachmentId;
          // Find the attachment URL from the timeline entry
          const timelineEntry = timeline.find((e) => e.id === timelineEntryId);
          const disputeAttachment = timelineEntry?.attachments.find(
            (a) => a.id === targetAttachmentId,
          );
          if (!disputeAttachment?.url) break;

          setActionLoading(true);
          try {
            // Convert dispute attachment to app Attachment shape and load PDF
            const appAttachment: AppAttachment = {
              id: disputeAttachment.id,
              name: disputeAttachment.filename,
              file: disputeAttachment.url,
              fileType: disputeAttachment.fileType,
              owner: null,
            };
            const pdfDoc = await pdfjs.getDocument(disputeAttachment.url).promise;
            const documents: LiftAIDocumentUntagged[] = new Array(pdfDoc.numPages)
              .fill(0)
              .map((_, i) => ({
                fileType: 'pdf' as const,
                name: disputeAttachment.filename,
                file: pdfDoc,
                pageNum: i + 1,
                attachment: appAttachment,
              }));
            setAdjustedUploadDocs(documents);
          } catch {
            // PDF loading failed — clear loading state
          } finally {
            setActionLoading(false);
          }
          break;
        }
        case 'review_adjusted_invoice': {
          const adjustedInvoiceId = payload?.adjustedInvoiceId as string | undefined;
          if (adjustedInvoiceId) {
            if (invoice?.id) {
              track('dispute_adjusted_invoice_review_clicked', {
                invoice_id: invoice.id,
                adjusted_invoice_id: adjustedInvoiceId,
              });
            }
            const url = buildUrlForInvoices({
              invoiceId: adjustedInvoiceId,
              kind: InvoiceKind.Invoice,
            });
            void navigate(url);
          }
          break;
        }
        case 'resolve': {
          const resolutionType = payload?.resolutionType as string | undefined;
          if (resolutionType === 'adjusted-invoice-received') {
            setResolvePreFill({
              type: 'adjusted-invoice-received',
              settledAmount: (payload?.settledAmount as number) ?? 0,
            });
          } else {
            setResolvePreFill({});
          }
          setActiveForm('resolve');
          break;
        }
        case 'withdraw':
          setWithdrawConfirmOpen(true);
          break;
        default:
          break;
      }
    },
    [
      onInitiateDispute,
      onGenerateDraft,
      handleSendEmail,
      navigate,
      timeline,
      suggestions,
      invoice?.id,
      track,
    ],
  );

  const handleAdjustedUploadDone = useCallback(
    (_taggedDocs: LiftAIDocumentTagged[]) => {
      // Invoice was created with parentInvoiceId — relationship is set by the backend.
      setAdjustedUploadDocs(null);
      // Refresh dispute state so suggestions update (e.g. move to "Review Adjusted Invoice")
      onRefreshState();
      // Refresh timeline to show the new "adjusted invoice linked" entry
      onRefreshTimeline();
    },
    [onRefreshState, onRefreshTimeline],
  );

  const resetForm = useCallback(() => {
    setActiveForm('none');
    setResolvePreFill({});
  }, []);

  // Dispute tab not available — show appropriate message based on invoice status
  // For approved invoices without a resolved dispute, show "not available" message
  // readOnly mode means we have a resolved dispute to show, so skip this check
  if (!canInitiateDispute && !readOnly) {
    const getDisputeUnavailableMessage = () => {
      if (!invoice) {
        return 'Invoice data is not available.';
      }
      if (invoice.status === 'approved') {
        return 'This invoice has been approved and can no longer be disputed. Disputes must be initiated before an invoice is approved.';
      }
      return 'Disputes are not available for this invoice.';
    };

    return (
      <Box sx={{ ...shellSx, p: 3, alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Disputes Not Available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
            {getDisputeUnavailableMessage()}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Loading state
  if (loading && !agentState) {
    return (
      <Box sx={{ ...shellSx, p: 3, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Error state
  if (error && !agentState) {
    return (
      <Box sx={{ ...shellSx, p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      {/* ── Beta notice ── */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>The Dispute Agent is in beta</AlertTitle>
        We&rsquo;re refining how the agent drafts emails and reads vendor replies, so behavior may
        change. We&rsquo;d love your feedback — send it to us through the Support button in the
        sidebar.
      </Alert>

      {/* ── Error banner ── */}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* ── State: None — review summary + agent suggestion ── */}
      {status === 'none' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          {invoice ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Review Summary
              </Typography>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <Typography variant="body2">
                  Invoice #{invoice.number} &middot; {invoice.property.name}
                </Typography>
                {invoice.proposedAmount != null && (
                  <Typography variant="h6" color="error.main">
                    {formatCurrency({ value: invoice.proposedAmount, currencyCode })}
                  </Typography>
                )}
              </Box>
              {invoice.serviceProvider ? (
                <Typography variant="caption" color="text.secondary">
                  {invoice.serviceProvider.name}
                </Typography>
              ) : null}
            </Paper>
          ) : null}
          {suggestions.length > 0 && !hasDraft ? (
            <SuggestionBanner
              suggestions={suggestions}
              onAction={handleAction}
              loading={actionLoading}
              vendorResponseSummary={latestVendorResponseSummary}
            />
          ) : null}
        </Box>
      )}

      {/* ── Status Header (all non-none states) ── */}
      {status !== 'none' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={getDisputeStatusLabel(status, invoice?.disputeNeedsAttention)}
                color={DISPUTE_STATUS_COLOR[status]}
                size="small"
              />
              {isActive &&
              agentState?.daysSinceLastContact != null &&
              agentState.daysSinceLastContact > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {agentState.daysSinceLastContact}d since last contact
                </Typography>
              ) : null}
              {loading ? <CircularProgress size={16} /> : null}
            </Box>
            {invoice?.proposedAmount != null && (
              <Typography variant="h6">
                {formatCurrency({ value: invoice.proposedAmount, currencyCode })}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {invoice?.serviceProvider ? (
                <Typography variant="caption" color="text.secondary">
                  {invoice.serviceProvider.name}
                </Typography>
              ) : null}
              {agentState?.resolution &&
              (status === 'resolved' || status === 'withdrawn') &&
              agentState.resolution.savings &&
              parseFloat(agentState.resolution.savings) > 0 ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Saved ${formatCurrency({ value: parseFloat(agentState.resolution.savings), currencyCode })}`}
                  color="success"
                  size="small"
                />
              ) : null}
            </Box>
            {agentState?.followUpCount != null && agentState.followUpCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                Follow-ups: {agentState.followUpCount}/{agentState.maxFollowUps}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* ── Agent Suggestions (non-none states, hidden when draft is showing or resolved) ── */}
      {suggestions.length > 0 &&
        status !== 'none' &&
        status !== 'resolved' &&
        status !== 'withdrawn' &&
        !hasDraft &&
        activeForm === 'none' && (
          <Box sx={{ mb: 2 }}>
            <SuggestionBanner
              suggestions={suggestions}
              onAction={handleAction}
              onWithdraw={() => setWithdrawConfirmOpen(true)}
              loading={actionLoading}
              vendorResponseSummary={latestVendorResponseSummary}
            />
          </Box>
        )}

      {/* ── Draft Editor (inline) ── */}
      {hasDraft && agentState?.currentDraft ? (
        <Box sx={{ mb: 2, ...sectionCardSx }}>
          <DraftEditor
            // Include invoice.id in the key so switching between two
            // invoices that *both* have cached SWR draft data (i.e.
            // the conditional unmount path doesn't kick in) still
            // forces a remount with the new invoice's draft as the
            // useState initial values. See draftGenerationKey above
            // for the per-invoice regenerate semantics.
            key={`draft-${invoice?.id ?? 'no-invoice'}-${draftGenerationKey.current}`}
            draft={agentState.currentDraft}
            invoiceAttachments={// Deduplicate by attachmentId (multi-page PDFs have multiple entries with same attachmentId)
            invoice?.attachments
              ?.filter(
                (a, index, self) =>
                  self.findIndex((b) => b.attachmentId === a.attachmentId) === index,
              )
              .map((a) => {
                // Extract filename from URL or use a fallback. attachmentUrl is
                // nullable on the wire (aligned with the generated read).
                const urlParts = (a.attachmentUrl ?? '').split('/');
                const filename = urlParts[urlParts.length - 1]?.split('?')[0] || 'attachment.pdf';
                return {
                  id: a.attachmentId,
                  filename,
                  // Leave url undefined (not '') when the wire has no URL, so the
                  // anchor below omits `href` and renders inert rather than
                  // self-navigating to the current page.
                  url: a.attachmentUrl ?? undefined,
                };
              })}
            contractInsights={invoice?.insights?.insights ?? []}
            sending={sendingEmail}
            onSend={handleSendEmail}
            onDiscard={onDiscardDraft}
            onUpdate={onUpdateDraft}
          />
        </Box>
      ) : null}

      {/* ── Inline Forms ── */}
      {activeForm === 'resolve' && (
        <Box sx={{ mb: 2, ...sectionCardSx }}>
          <ResolveForm
            onSubmit={async (type, amount, notes) => {
              await onResolve(type, amount, notes);
              resetForm();
            }}
            onCancel={resetForm}
            initialType={resolvePreFill.type}
            initialSettledAmount={resolvePreFill.settledAmount}
          />
        </Box>
      )}
      {/* ── Resolution Details (terminal states) ── */}
      {agentState?.resolution && (status === 'resolved' || status === 'withdrawn') ? (
        <Box sx={{ mb: 2 }}>
          <ResolutionDetails
            resolution={agentState.resolution}
            status={status}
            onReopen={readOnly ? undefined : () => setReopenConfirmOpen(true)}
            reopenLoading={reopenLoading}
          />
        </Box>
      ) : null}

      {/* ── Communication Timeline ── */}
      {timeline.length > 0 && (
        <Box sx={{ mb: 2, ...sectionCardSx }}>
          <Timeline entries={timeline} isMonitoring={isMonitoring} />
        </Box>
      )}

      {/* ── Monitoring state with no timeline ── */}
      {isMonitoring && timeline.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            py: 6,
            ...sectionCardSx,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'white',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: `${pulse} 2s infinite`,
                mb: 2,
              }}
            >
              <RadarIcon />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Waiting for vendor response...
            </Typography>
          </Box>
        </Box>
      ) : null}

      {/* ── Manual Actions (active states) ── */}
      {isActive && activeForm === 'none' && !hasDraft ? (
        <Box sx={sectionCardSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="text"
              color="error"
              size="small"
              startIcon={<CloseIcon />}
              onClick={() => setWithdrawConfirmOpen(true)}
              sx={{ ...BUTTON_SX, textTransform: 'none' }}
            >
              Withdraw Dispute
            </Button>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => setActiveForm('resolve')}
              sx={{ ...BUTTON_SX, textTransform: 'none' }}
            >
              Mark Resolved
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* ── Withdraw Confirmation Modal ── */}
      <Dialog open={withdrawConfirmOpen} onClose={() => setWithdrawConfirmOpen(false)}>
        <DialogTitle>Withdraw Dispute?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to withdraw this dispute? This action cannot be undone and the
            dispute will be closed without resolution.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWithdrawConfirmOpen(false)}
            color="inherit"
            sx={{ ...BUTTON_SX, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setWithdrawConfirmOpen(false);
              await onWithdraw();
            }}
            color="error"
            variant="contained"
            sx={{ ...BUTTON_SX, textTransform: 'none' }}
          >
            Withdraw Dispute
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reopen Confirmation Modal ── */}
      <Dialog open={reopenConfirmOpen} onClose={() => setReopenConfirmOpen(false)}>
        <DialogTitle>Reopen Dispute?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reopen this dispute? The previous resolution will be cleared
            and the dispute will return to an active state.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReopenConfirmOpen(false)}
            color="inherit"
            sx={{ ...BUTTON_SX, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setReopenConfirmOpen(false);
              setReopenLoading(true);
              try {
                await onReopen();
              } finally {
                setReopenLoading(false);
              }
            }}
            color="primary"
            variant="contained"
            sx={{ ...BUTTON_SX, textTransform: 'none' }}
          >
            Reopen Dispute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Adjusted Invoice dialog */}
      {adjustedUploadDocs ? (
        <SelectUploadDocumentTypeDialog
          open
          onCancel={() => setAdjustedUploadDocs(null)}
          onDone={handleAdjustedUploadDone}
          documents={adjustedUploadDocs}
          initialDocumentType={UploadDocumentType.invoice}
          initialFormData={
            invoice?.id
              ? {
                  parentInvoiceId: invoice.id,
                  adjustmentSource: 'agent' as const,
                  propertyId: invoice.property?.id,
                }
              : undefined
          }
        />
      ) : null}
    </Box>
  );
};

export default DisputeAgentPanel;
